import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Mail, Phone } from "lucide-react";
import { teamMembers } from "@/data/team";
import { useTranslation } from "@/i18n";

const API = `/api`;

export default function Profil() {
  const { slug } = useParams();
  const { lang, t } = useTranslation();
  const [members, setMembers] = useState(teamMembers);

  useEffect(() => {
    fetch(`${API}/team`)
      .then((r) => r.json())
      .then((data) => {
        if (Array.isArray(data) && data.length > 0) setMembers(data);
      })
      .catch(() => {});
  }, []);

  const member = members.find((m) => m.slug === slug);
  const [copied, setCopied] = useState(false);

  const memberEmail = member?.email || "info@e2.law";
  const copyEmail = () => {
    try {
      navigator.clipboard.writeText(memberEmail);
      setCopied(true);
      setTimeout(() => setCopied(false), 5000);
    } catch {}
  };

  if (!member) {
    return (
      <div className="pt-40 pb-32 min-h-screen text-center" data-testid="profil-notfound">
        <p className="font-serif text-2xl">{t("profil.notFound")}</p>
        <Link to="/" className="mt-6 inline-block text-gold text-sm underline underline-offset-4">
          {t("profil.backHome")}
        </Link>
      </div>
    );
  }

  return (
    <div className="pt-40 pb-24 sm:pb-32 min-h-screen" data-testid="profil-page">
      <div className="max-w-[1400px] mx-auto px-6 sm:px-12 lg:px-20">
        <Link
          to="/#equipe"
          data-testid="profil-back-link"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#A0A5B1] hover:text-gold transition-colors duration-300"
        >
          <ArrowLeft className="w-4 h-4" /> {t("profil.back")}
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start"
        >
          <div className="lg:col-span-5">
            <div className="relative border border-gold/25 p-3 max-w-md">
              <div className="overflow-hidden">
                <img
                  src={member.image}
                  alt={member.name}
                  className="w-full aspect-[3/4] object-cover grayscale"
                  data-testid="profil-photo"
                />
              </div>
              <span className="absolute -bottom-3 left-6 bg-ink px-3 font-mono text-[10px] uppercase tracking-[0.25em] text-gold">
                {member.barreau}
              </span>
            </div>
          </div>

          <div className="lg:col-span-7 lg:pt-8">
            <span className="label-accent" data-testid="profil-role">{t(`roles.${member.role}`)}</span>
            <h1 className="mt-5 font-serif font-light tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-tight" data-testid="profil-name">
              {member.name}
            </h1>
            <div className="mt-8 flex flex-wrap gap-x-10 gap-y-4">
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#9BA1B0]">{t("profil.languages")}</p>
                <p className="mt-1 text-sm text-[#FAF8F5]">{member.languages}</p>
              </div>
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#9BA1B0]">{t("profil.cabinet")}</p>
                <p className="mt-1 text-sm text-[#FAF8F5]">{t("profil.cabinetValue")}</p>
              </div>
            </div>

            <p className="mt-8 text-sm sm:text-base text-[#A0A5B1] leading-relaxed max-w-xl" data-testid="profil-bio">
              {lang === "en" ? member.bio_en || member.bio : lang === "nl" ? member.bio_nl || member.bio : member.bio}
            </p>

            <div className="mt-10 flex flex-wrap gap-4">
              <a
                href={`mailto:${memberEmail}`}
                onClick={copyEmail}
                data-testid="profil-email-cta"
                className="inline-flex items-center gap-2 bg-gold text-ink text-xs uppercase tracking-[0.25em] font-medium px-6 py-3.5 hover:bg-gold-light transition-colors"
              >
                <Mail className="w-4 h-4" /> {t("profil.writeTo")} {member.name.split(" ")[0]}
              </a>
              <a
                href={`tel:${(member.phone || "02/880.64.02").replace(/[^+\d]/g, "")}`}
                data-testid="profil-phone-cta"
                className="inline-flex items-center gap-2 border border-gold/40 text-gold text-xs uppercase tracking-[0.25em] px-6 py-3.5 hover:bg-gold/10 transition-colors"
              >
                <Phone className="w-4 h-4" /> {member.phone || "02/880.64.02"}
              </a>
            </div>
            {copied && (
              <p className="mt-4 text-xs text-gold/90 tracking-wide" data-testid="profil-email-copied">
                {t("profil.emailHint")} <span className="font-mono">{memberEmail}</span>
              </p>
            )}
          </div>
        </motion.div>

        <div className="mt-20 bg-ink-card border border-gold/20 p-8 sm:p-12 flex flex-wrap items-center justify-between gap-6" data-testid="profil-cta">
          <div className="max-w-xl">
            <span className="label-accent">{t("profil.ctaOverline")}</span>
            <p className="mt-4 font-serif text-2xl sm:text-3xl leading-snug">
              {t("profil.ctaTitle")}{" "}
              <em className="italic text-gold-gradient not-italic">{member.name.split(" ")[0]}</em>
            </p>
            <p className="mt-3 text-sm text-[#A0A5B1] leading-relaxed">
              {t("profil.ctaBody")}
            </p>
          </div>
          <a
            href="/#contact"
            data-testid="profil-cta-contact"
            className="inline-flex items-center gap-2 bg-gold text-ink text-xs uppercase tracking-[0.25em] font-medium px-8 py-4 hover:bg-gold-light transition-colors shrink-0"
          >
            {t("profil.ctaButton")}
          </a>
        </div>
      </div>
    </div>
  );
}
